/* eslint-disable react/prop-types */
import React, { ReactNode, Fragment, Suspense } from 'react';

import { v4 as uid } from 'uuid';

import ModalContext from '../../hooks/useModal/ModalContext';
import {
  DestroyByRootIdFn,
  DestroyFn,
  HideFn,
  ShowFn,
  UpdateFn
} from '../../hooks/useModal/ModalContextTypes';
import reducer, { initialState, ModalActions } from './ModalReducer';

interface TransitionProps {
  onExited?: (...args: unknown[]) => void;
}

interface ModalProps {
  TransitionProps?: TransitionProps;
  onClose?: (...args: unknown[]) => void;
  onExited?: (...args: unknown[]) => void;
  [key: string]: any;
}

export interface ModalProviderProps {
  children: ReactNode;
  legacy?: boolean;
  suspense?: boolean;
  fallback?: ReactNode | null;
}

export default function ModalProvider({
  children,
  legacy = false,
  suspense = true,
  fallback = null
}: ModalProviderProps) {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const MISSED_MODAL_ID_ERROR_MESSAGE = '[ERROR]: Modal ID is missing';
  const MISSED_MODAL_ROOT_ID_ERROR_MESSAGE = '[ERROR]: Modal root ID is missing';

  const update = React.useCallback<UpdateFn>(
    (id: string, props: ModalProps) => {
      if (!id) {
        console.error(MISSED_MODAL_ID_ERROR_MESSAGE);
        return;
      }

      dispatch({
        type: ModalActions.UPDATE,
        payload: { id, props }
      });
    },
    [dispatch]
  );

  const hide = React.useCallback<HideFn>(
    (id: string) => {
      if (!id) {
        console.error(MISSED_MODAL_ID_ERROR_MESSAGE);
        return;
      }
      dispatch({ type: ModalActions.HIDE, payload: { id } });
    },
    [dispatch]
  );

  const destroy = React.useCallback<DestroyFn>(
    (id: string) => {
      if (!id) {
        console.error(MISSED_MODAL_ID_ERROR_MESSAGE);
        return;
      }
      dispatch({ type: ModalActions.DESTROY, payload: { id } });
    },
    [dispatch]
  );

  const destroyByRootId = React.useCallback<DestroyByRootIdFn>(
    (rootId: string) => {
      if (!rootId) {
        console.error(MISSED_MODAL_ROOT_ID_ERROR_MESSAGE);
        return;
      }
      dispatch({ type: ModalActions.DESTROY_BY_ROOT_ID, payload: { rootId } });
    },
    [dispatch]
  );

  const show = React.useCallback<ShowFn>(
    (component, props, options) => {
      let id = uid();
      if (options?.rootId) {
        id = `${options.rootId}.${id}`;
      }

      dispatch({
        type: ModalActions.SHOW,
        payload: { id, component, props, options }
      });

      return {
        id,
        hide: () => hide(id),
        destroy: () => destroy(id),
        update: newProps => update(id, newProps)
      };
    },
    [dispatch, hide, destroy, update]
  );

  const renderState = () =>
    Object.keys(state).map(id => {
      const {
        component: Component,
        props,
        options
      } = state[id] as { component: any; props: ModalProps; options?: any };

      const handleClose = (...args: unknown[]) => {
        if (options?.destroyOnClose) {
          destroy(id);
        } else {
          hide(id);
        }
        if (props?.onClose) {
          props.onClose(...args);
        }
      };

      const handleExited = (...args: unknown[]) => {
        props?.onExited?.(...args);
        props?.TransitionProps?.onExited?.(...args);
        destroy(id);
      };

      const extraProps = {
        TransitionProps: {
          ...(props?.TransitionProps || {}),
          onExited: handleExited
        }
      };

      return (
        <div key={id} style={{ position: 'relative', zIndex: options?.zIndex || 'auto' }}>
          <Component {...props} onClose={handleClose} {...extraProps} />
        </div>
      );
    });

  const SuspenseWrapper = suspense ? Suspense : Fragment;

  return (
    <ModalContext.Provider
      value={{
        state,
        updateModal: update,
        hideModal: hide,
        destroyModal: destroy,
        showModal: show,
        destroyModalsByRootId: destroyByRootId
      }}
    >
      {children}
      <SuspenseWrapper fallback={fallback}>{renderState()}</SuspenseWrapper>
    </ModalContext.Provider>
  );
}
